import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, Feather, Search } from "lucide-react";
import { PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CategoryManager } from "@/components/CategoryManager";
import { RebuildAllButton } from "@/components/RebuildAllButton";
import { NewProductDialog } from "@/components/NewProductDialog";
import { AuthImg } from "@/components/studio/AuthImg";
import { api, fmtDate, PRODUCT_TYPES } from "@/lib/api";

const typeLabel = (v) => PRODUCT_TYPES.find((t) => t.value === v)?.label || v;

const ProductCard = ({ p, catName, theme, i }) => (
  <Link
    to={`/products/${p.id}`}
    data-testid={`product-card-${p.id}`}
    className="group rise block"
    style={{ animationDelay: `${Math.min(i, 8) * 0.05}s` }}
  >
    {p.files?.preview_paths?.length ? (
      <div className="aspect-[210/297] border border-[#2A2325]/10 shadow-sm overflow-hidden bg-[#F5EFE6] transition-transform duration-300 group-hover:-translate-y-1 group-hover:shadow-md">
        <AuthImg path={`/products/${p.id}/pdf-preview/1.png`} version={p.files.preview_paths[0]} alt={`${p.title} cover`} className="w-full h-full object-cover" testId={`product-cover-img-${p.id}`} />
      </div>
    ) : (
    <div data-testid={`product-cover-placeholder-${p.id}`} className="aspect-[210/297] border border-[#2A2325]/10 relative p-4 flex flex-col justify-between shadow-sm transition-transform duration-300 group-hover:-translate-y-1 group-hover:shadow-md" style={{ background: theme?.dark || "#38171E" }}>
      <div className="absolute inset-2.5 border pointer-events-none" style={{ borderColor: `${theme?.accent || "#D4AF37"}88` }} />
      <div className="text-center text-[0.55rem] mt-3 tracking-[0.22em] uppercase" style={{ color: theme?.accent }}>{p.series || catName}{p.issue_no ? ` · No. ${p.issue_no}` : ""}</div>
      <div className="text-center">
        <div className="mb-3" style={{ color: theme?.accent }}>❦</div>
        <div className="font-playfair text-lg leading-tight line-clamp-4 text-[#F3EBD6]">{p.title}</div>
        {p.subtitle && <div className="font-body-serif italic text-xs text-[#F3EBD6]/70 mt-2 line-clamp-2">{p.subtitle}</div>}
      </div>
      <div className="text-center text-[8px] tracking-[0.3em] mb-2" style={{ color: theme?.accent }}>SOFT SPINE PRESS</div>
    </div>
    )}
    <div className="mt-3 flex items-center justify-between text-xs text-[#5C5255]">
      <span>{typeLabel(p.product_type)} · {p.section_count} sections</span>
      {p.published ? (
        <span className="px-2 py-0.5 bg-[#2D5A3F] text-white" data-testid={`product-live-badge-${p.id}`}>Live</span>
      ) : p.files?.pdf_path ? (
        <span className="px-2 py-0.5 bg-[#E8EFE9] text-[#2A4731]" data-testid={`product-ready-badge-${p.id}`}>Ready</span>
      ) : (
        <span className="px-2 py-0.5 bg-[#F5EFE6]">Draft</span>
      )}
    </div>
    <div className="text-[11px] text-[#5C5255]/80 mt-1">Edited {fmtDate(p.updated_at)}</div>
  </Link>
);

const CategoryGroup = ({ id, name, theme, items, open, onToggle }) => (
  <section className="border-t border-[#2A2325]/10" data-testid={`library-group-${id}`}>
    <button onClick={onToggle} aria-expanded={open} className="w-full flex items-center gap-3 py-4 text-left group" data-testid={`library-group-toggle-${id}`}>
      <span className="w-2.5 h-2.5 shrink-0" style={{ background: theme?.accent || "#D4AF37" }} />
      <span className="font-playfair text-xl text-[#2A2325] group-hover:text-[#5E2B38] transition-colors">{name}</span>
      <span className="text-xs text-[#5C5255]" data-testid={`library-group-count-${id}`}>{items.length} {items.length === 1 ? "title" : "titles"}</span>
      <ChevronDown size={18} className={`ml-auto text-[#5C5255] transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
    </button>
    {open && (
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6 max-w-4xl pb-8" data-testid={`library-group-grid-${id}`}>
        {items.map((p, i) => <ProductCard key={p.id} p={p} i={i} catName={name} theme={theme} />)}
      </div>
    )}
  </section>
);

export default function Library() {
  const [cats, setCats] = useState([]);
  const [items, setItems] = useState([]);
  const [cat, setCat] = useState("");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState({});

  const loadCats = useCallback(() => api.get("/categories").then((r) => setCats(r.data)), []);
  useEffect(() => {
    loadCats();
  }, [loadCats]);
  useEffect(() => {
    const t = setTimeout(() => {
      api.get("/products", { params: { category_id: cat || undefined, q: q || undefined } }).then((r) => setItems(r.data));
    }, 200);
    return () => clearTimeout(t);
  }, [cat, q]);

  const groups = [...cats.map((c) => ({ id: c.id, name: c.name, theme: c.theme })), { id: "none", name: "No category" }]
    .map((g) => ({ ...g, items: items.filter((p) => (cats.some((c) => c.id === p.category_id) ? p.category_id : "none") === g.id) }))
    .filter((g) => g.items.length);
  const isOpen = (id) => open[id] ?? Boolean(q || cat);

  return (
    <div>
      <PageHeader eyebrow="Books, guides & digital products" title="The Library">
        <CategoryManager cats={cats} onChange={loadCats} />
        <RebuildAllButton />
        <NewProductDialog defaultCategory={cat} trigger={<Button className="rounded-none gap-2" data-testid="library-new-product-btn"><Feather size={16} /> New title</Button>} />
      </PageHeader>
      <div className="px-6 md:px-12 py-8">
        <div className="flex flex-col lg:flex-row lg:items-center gap-4 justify-between mb-10">
          <div className="flex flex-wrap gap-2" data-testid="category-filter">
            <button onClick={() => setCat("")} data-testid="category-filter-all" className={`px-4 py-1.5 text-sm border transition-colors ${!cat ? "bg-[#5E2B38] text-[#FFFDF9] border-[#5E2B38]" : "border-[#2A2325]/15 hover:border-[#5E2B38]"}`}>All</button>
            {cats.map((c) => (
              <button key={c.id} onClick={() => setCat(c.id)} data-testid={`category-filter-${c.id}`} className={`px-4 py-1.5 text-sm border transition-colors ${cat === c.id ? "bg-[#5E2B38] text-[#FFFDF9] border-[#5E2B38]" : "border-[#2A2325]/15 hover:border-[#5E2B38]"}`}>
                {c.name} <span className="opacity-60 ml-1">{c.product_count}</span>
              </button>
            ))}
          </div>
          <div className="relative w-full lg:w-72">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5255]" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search titles" className="pl-9 bg-white rounded-none" data-testid="library-search-input" />
          </div>
        </div>
        {items.length ? (
          <div className="border-b border-[#2A2325]/10" data-testid="library-groups">
            <div className="flex justify-end gap-4 text-xs text-[#5C5255] mb-2">
              <button onClick={() => setOpen(Object.fromEntries(groups.map((g) => [g.id, true])))} className="hover:text-[#5E2B38] hover:underline" data-testid="library-expand-all">Expand all</button>
              <button onClick={() => setOpen(Object.fromEntries(groups.map((g) => [g.id, false])))} className="hover:text-[#5E2B38] hover:underline" data-testid="library-collapse-all">Collapse all</button>
            </div>
            {groups.map((g) => <CategoryGroup key={g.id} {...g} open={isOpen(g.id)} onToggle={() => setOpen((o) => ({ ...o, [g.id]: !isOpen(g.id) }))} />)}
          </div>
        ) : (
          <p className="font-body-serif italic text-lg text-[#5C5255] py-20 text-center" data-testid="library-empty">No titles here yet.</p>
        )}
      </div>
    </div>
  );
}
